<template>
  <BaseSideModal :width="'820px'" @close="$emit('close')">
    <template #title>
      <span>{{ $t('carriers.import.runRows.Title') }}</span>
    </template>

    <!-- See AllUpdatesDrawer: a second line in the title slot overflows the
         modal, because its content height assumes a one-line header. -->
    <p class="run-rows__subtitle">
      {{ carrierName }} · {{ formatWhen(when, $i18n.locale) }}
    </p>

    <!-- One tab per answer, each counted by the server over the whole read. A tab with
         nothing in it is not shown; All always is. -->
    <div class="run-rows__tabs">
      <BaseTabs>
        <BaseTabsTab
          v-for="t in tabs"
          :key="t.key"
          :active="filter === t.key"
          :badge-text="t.count === null ? '' : formatCount(t.count)"
          @click="switchFilter(t.key)"
        >
          {{ $t(`carriers.import.runRows.filter.${t.key}`) }}
        </BaseTabsTab>
      </BaseTabs>
    </div>

    <div class="run-rows__search">
      <InputSearch
        :value="search"
        :placeholder="$t('carriers.import.runRows.Search')"
        :opened="true"
        @searchUpdate="onSearch"
      />
    </div>

    <BasePreloader v-if="loading && !shown.length" color="purple" size="l"/>

    <p v-else-if="loadFailed && !shown.length" class="run-rows__empty">
      {{ $t('adminTitles.someError') }}
      <BaseButton type="outline" is-small @click="reload">{{ $t('adminTitles.TryAgain') }}</BaseButton>
    </p>

    <p v-else-if="!shown.length && !hasMore" class="run-rows__empty">{{ $t('carriers.import.runRows.Empty') }}</p>

    <template v-else>
      <div v-for="item in shown" :key="item.key" class="run-rows__row">
        <div class="run-rows__main">
          <p class="run-rows__policy">
            <a
              v-if="item.policyId"
              class="run-rows__policy-link"
              :href="policyHref(item.policyId)"
              target="_blank"
              rel="noopener"
              :title="$t('carriers.import.snapshot.OpenPolicy')"
            >{{ item.policyNumber }} ↗</a>
            <template v-else>{{ item.policyNumber }}</template>
          </p>
          <p v-if="item.clientName" class="run-rows__client">
            <a
              v-if="item.profileId"
              class="run-rows__policy-link"
              :href="profileHref(item.profileId)"
              target="_blank"
              rel="noopener"
            >{{ item.clientName }}</a>
            <template v-else>{{ item.clientName }}</template>
          </p>

          <p v-if="item.hint" class="run-rows__why">{{ $t(item.hint) }}</p>
          <p v-if="item.paymentsNote" class="run-rows__why">{{ item.paymentsNote }}</p>
          <p v-for="(why, i) in item.reasons" :key="i" class="run-rows__why">{{ whyText(why) }}</p>
          <p v-if="item.why" class="run-rows__why">{{ item.why }}</p>
          <p v-if="item.error" class="run-rows__failure">{{ item.error }}</p>

          <p v-if="item.changes" class="run-rows__changes">{{ item.changes }}</p>

          <!-- What else the read found about the same policy, in All: the other half's answer. -->
          <p v-if="item.tags.length" class="run-rows__tags">
            <span v-for="tag in item.tags" :key="tag" class="run-rows__tag">{{ $t(tag) }}</span>
          </p>
        </div>

        <div class="run-rows__side">
          <span class="run-rows__result" :class="`run-rows__result--${item.tone}`">{{ $t(item.label) }}</span>
          <span v-if="item.status" class="run-rows__status">{{ $t(item.status) }}</span>
        </div>
      </div>

      <!-- The next page of 50 arrives when this scrolls into view. -->
      <div v-if="hasMore" ref="more" class="run-rows__more">
        <BasePreloader v-if="loading" color="purple" size="s"/>
      </div>
    </template>

    <template #footer>
      <BaseButton
        v-if="payments"
        type="outline"
        :loading="downloading"
        @click="download"
      >{{ $t('carriers.import.payments.DownloadAllPayments') }}</BaseButton>
      <BaseButton type="outline" @click="$emit('close')">{{ $t('buttons.back') }}</BaseButton>
    </template>
  </BaseSideModal>
</template>

<script>
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import BaseTabs from '@/views/RenovaDashboard/components/ui-kit/BaseTabs';
import BaseTabsTab from '@/views/RenovaDashboard/components/ui-kit/BaseTabsTab';
import InputSearch from '@/views/RenovaDashboard/components/ui-kit/Inputs/InputSearch';
import {
  apiError, formatCount, formatWhen, isSnapshotRun, readTwinOf, saveFile,
} from '@/utils/carrierImport';
import { formatCarrierDate } from '@/utils/carrierFormat';
import {
  READ_ROW_TABS, RUN_ROW_FILTERS, SNAPSHOT_ROW_FILTERS, runRowResult, snapshotRowResult,
} from '@/utils/carrierPayments';

const PAGE = 50;
const SEARCH_DELAY = 300;

/** A policy number as both halves of a read can agree on it. */
const numberKey = (n) => String(n || '').replace(/[\s-]/g, '').toUpperCase();

/**
 * Every policy one read went through, from both its halves, and what it did with each.
 *
 * The payments half's reconcile rows say what happened to each policy's payments; the
 * policies half's staged rows say which policies are new, which had their details changed
 * and which could not be read. One list with a tab per answer, so a read is looked at in one
 * place rather than one window per half.
 *
 * All walks the payments rows first and the staged rows after; a staged row for a policy the
 * payments half already listed is folded into that row as a tag, so each policy shows once.
 *
 * Read-only. Decisions stay in Needs review.
 */
export default {
  name: 'ReadPoliciesModal',
  components: {
    BaseSideModal, BaseButton, BasePreloader, BaseTabs, BaseTabsTab, InputSearch,
  },
  props: {
    /** The payments half of the read, or null. */
    payments: { type: Object, default: null },
    /** The policies half of the read, or null. Only a staged (snapshot) read has rows to list. */
    policies: { type: Object, default: null },
    carrierName: { type: String, default: '' },
  },
  emits: ['close'],
  data() {
    return {
      /** Normalised rows of the tab showing. */
      items: [],
      /** A key of READ_ROW_TABS. */
      filter: 'ALL',
      search: '',
      /** Which of the tab's sources is being paged, and where in it. */
      sourceIndex: 0,
      page: 0,
      offset: 0,
      loading: true,
      loadFailed: false,
      /** Tab key -> its count; absent until it is in. */
      counts: {},
      /** The payments summary's policies the carrier stopped billing, once asked for. */
      stopped: null,
      loadSeq: 0,
      countSeq: 0,
      searchTimer: null,
      observer: null,
      downloading: false,
    };
  },
  computed: {
    when() {
      const run = this.payments || this.policies || {};
      return run.finishedAt || run.updatedAt;
    },
    hasSnapshot() {
      return Boolean(this.policies && isSnapshotRun(this.policies));
    },
    /** The tabs this read can fill, and of them the ones with something in them. */
    tabs() {
      return READ_ROW_TABS
        .filter((t) => t.key === 'ALL' || this.sourceAvailable(t.source))
        .map((t) => ({ key: t.key, count: t.key in this.counts ? this.counts[t.key] : null }))
        .filter((t) => t.key === 'ALL' || t.key === this.filter || t.count > 0)
        .map((t) => (t.key === 'ALL' ? { ...t, count: null } : t));
    },
    /** Where the showing tab's rows come from, in order. */
    sources() {
      if (this.filter === 'ALL') {
        return [
          this.payments ? { source: 'payments', filter: RUN_ROW_FILTERS.ALL } : null,
          this.hasSnapshot ? { source: 'snapshot', filter: SNAPSHOT_ROW_FILTERS.ALL } : null,
        ].filter(Boolean);
      }
      const tab = READ_ROW_TABS.find((t) => t.key === this.filter);
      if (!tab || !this.sourceAvailable(tab.source)) return [];
      if (tab.source === 'payments') return [{ source: 'payments', filter: RUN_ROW_FILTERS[tab.key] }];
      if (tab.source === 'snapshot') return [{ source: 'snapshot', filter: SNAPSHOT_ROW_FILTERS[tab.key] }];
      return [{ source: 'stopped' }];
    },
    hasMore() {
      return this.sourceIndex < this.sources.length;
    },
    /** The staged rows have no server-side search: they are narrowed here, as they arrive. */
    shown() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.items;
      return this.items.filter((item) => item.source === 'payments'
        || `${item.policyNumber} ${item.clientName || ''}`.toLowerCase().includes(q));
    },
  },
  created() {
    this.reload();
  },
  mounted() {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0] && entries[0].isIntersecting && !this.loading && !this.loadFailed && this.hasMore) this.load();
    });
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.searchTimer);
  },
  methods: {
    formatCount,
    formatWhen,
    sourceAvailable(source) {
      if (source === 'snapshot') return this.hasSnapshot;
      return Boolean(this.payments);
    },
    policyHref(id) {
      return this.$router.resolve({ name: 'policy', params: { id } }).href;
    },
    profileHref(id) {
      return this.$router.resolve({ name: 'userDetail', params: { id } }).href;
    },
    /** The same words the held policies use. */
    whyText(why) {
      const carrier = this.carrierName;
      if (why.kind === 'applyError') return this.$t('carriers.import.held.ApplyFailed', { error: why.message });
      if (why.kind === 'keptInRenova') {
        const key = `${why.status === 'CANCELED' ? 'KeptCanceled' : 'KeptPaid'}${why.receipt ? '' : 'Unlisted'}`;
        return this.$t(`carriers.import.held.${key}`, { date: formatCarrierDate(why.dueDate), carrier });
      }
      const params = { row: why.row, carrier, reason: why.reason };
      return why.reason
        ? this.$t('carriers.import.held.Unreadable', params)
        : this.$t('carriers.import.held.UnreadableNoReason', params);
    },
    /** "3 payments marked paid · 1 payments added · savings figures refreshed", or "If you apply: …". */
    changesText(result) {
      const pieces = result.changes.map((p) => `${formatCount(p.count)} ${this.$t(p.label)}`);
      if (result.savingsUpdated) pieces.push(this.$t('carriers.import.runRows.SavingsRefreshed'));
      if (!pieces.length) return '';
      const text = pieces.join(' · ');
      return result.changesLead ? this.$t('carriers.import.held.IfApplied', { changes: text }) : text;
    },
    skipText(reason) {
      const key = `carriers.import.snapshot.skip.${reason}`;
      return this.$te(key) ? this.$t(key) : '';
    },
    fromPayment(row) {
      const result = runRowResult(row);
      const d = (row.detail && typeof row.detail === 'object') ? row.detail : {};
      return {
        key: `p-${row.id}`,
        source: 'payments',
        policyNumber: row.policyNumber,
        clientName: row.clientName,
        policyId: row.insurancePolicyId || null,
        profileId: null,
        label: result.label,
        tone: result.tone,
        status: null,
        hint: result.hint,
        paymentsNote: result.paymentsNote,
        reasons: result.reasons,
        why: '',
        error: result.error,
        changes: this.changesText(result),
        tags: d.carrierStopped ? ['carriers.import.runRows.filter.STOPPED'] : [],
      };
    },
    fromSnapshot(row) {
      const result = snapshotRowResult(row);
      return {
        key: `s-${row.id}`,
        source: 'snapshot',
        policyNumber: row.policyNumber,
        clientName: row.clientName,
        policyId: row.appliedPolicyId || row.matchedPolicyId || null,
        profileId: row.personProfileId || null,
        label: result.label,
        tone: result.tone,
        status: result.status,
        hint: null,
        paymentsNote: null,
        reasons: [],
        // The reason in words only: the worker's own detail is a technical line.
        why: row.skipReason ? this.skipText(row.skipReason) : '',
        error: null,
        changes: '',
        tags: [],
      };
    },
    fromStopped(policy) {
      return {
        key: `x-${policy.insurancePolicyId || policy.policyNumber}`,
        source: 'stopped',
        policyNumber: policy.policyNumber,
        clientName: policy.clientName,
        policyId: policy.insurancePolicyId || null,
        profileId: null,
        label: 'carriers.import.runRows.filter.STOPPED',
        tone: 'warn',
        status: null,
        hint: null,
        paymentsNote: null,
        reasons: [],
        why: '',
        error: null,
        changes: '',
        tags: [],
      };
    },
    /**
     * Staged rows into the list. In All, a staged row for a policy the payments half already
     * listed adds its answer to that row instead of listing the policy twice; the payments
     * rows are all in by then, because All pages them first. Same number is not enough: the
     * row must also be the same policy or holder (readTwinOf).
     */
    addSnapshotRows(rows) {
      const byNumber = {};
      if (this.filter === 'ALL') {
        this.items.forEach((item) => {
          if (item.source !== 'payments') return;
          const key = numberKey(item.policyNumber);
          (byNumber[key] = byNumber[key] || []).push(item);
        });
      }
      const fresh = [];
      rows.forEach((row) => {
        const item = this.fromSnapshot(row);
        const twin = readTwinOf(byNumber[numberKey(row.policyNumber)], item);
        if (!twin) {
          fresh.push(item);
          return;
        }
        if (!twin.tags.includes(item.label)) twin.tags.push(item.label);
        if (!twin.profileId) twin.profileId = item.profileId;
        if (!twin.policyId) twin.policyId = item.policyId;
      });
      this.items = this.items.concat(fresh);
    },
    switchFilter(key) {
      if (key === this.filter) return;
      this.filter = key;
      this.load(true);
    },
    onSearch(value) {
      this.search = value || '';
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.reload, SEARCH_DELAY);
    },
    reload() {
      this.load(true);
      this.countAll();
    },
    /** One row per tab, for its total: the tabs' badges. Payments counts follow the search. */
    countAll() {
      this.countSeq += 1;
      const seq = this.countSeq;
      this.counts = {};
      const set = (key, n) => {
        if (seq !== this.countSeq) return;
        this.counts = { ...this.counts, [key]: Number(n) || 0 };
      };
      READ_ROW_TABS.forEach((tab) => {
        if (tab.key === 'ALL' || !this.sourceAvailable(tab.source)) return;
        if (tab.source === 'payments') {
          this.$store.dispatch('carrierImportFetchPaymentRows', {
            scrapeJobId: this.payments.scrapeJobId,
            searchQuery: this.search.trim() || undefined,
            ...RUN_ROW_FILTERS[tab.key],
            page: 0,
            limit: 1,
          })
            .then((data) => set(tab.key, data && data.totalCount))
            .catch(() => {});
        } else if (tab.source === 'snapshot') {
          this.$store.dispatch('carrierImportFetchSnapshot', {
            scrapeJobId: this.policies.scrapeJobId, ...SNAPSHOT_ROW_FILTERS[tab.key], limit: 1, offset: 0,
          })
            .then((data) => set(tab.key, data && data.total))
            .catch(() => {});
        } else {
          this.loadStopped().then((list) => set(tab.key, list.length)).catch(() => {});
        }
      });
    },
    /** The payments summary's stopped-billing policies, asked for once. */
    loadStopped() {
      if (this.stopped) return Promise.resolve(this.stopped);
      return this.$store.dispatch('carrierImportFetchPaymentSummary', this.payments.scrapeJobId)
        .then((data) => {
          this.stopped = (data && data.stoppedPolicies) || [];
          return this.stopped;
        });
    },
    /** The next page of the tab showing, or its first after a change. */
    load(reset = false) {
      if (reset) {
        this.items = [];
        this.sourceIndex = 0;
        this.page = 0;
        this.offset = 0;
      }
      const current = this.sources[this.sourceIndex];
      if (!current) {
        this.loading = false;
        return Promise.resolve();
      }
      this.loading = true;
      this.loadFailed = false;
      this.loadSeq += 1;
      const seq = this.loadSeq;
      const next = () => {
        this.sourceIndex += 1;
        this.page = 0;
        this.offset = 0;
      };
      let request;
      if (current.source === 'payments') {
        request = this.$store.dispatch('carrierImportFetchPaymentRows', {
          scrapeJobId: this.payments.scrapeJobId,
          searchQuery: this.search.trim() || undefined,
          ...current.filter,
          page: this.page,
          limit: PAGE,
        }).then((data) => {
          if (seq !== this.loadSeq) return;
          const rows = (data && data.items) || [];
          this.items = this.items.concat(rows.map(this.fromPayment));
          this.page += 1;
          const total = Number(data && data.totalCount) || 0;
          if (rows.length < PAGE || this.page * PAGE >= total) next();
        });
      } else if (current.source === 'snapshot') {
        request = this.$store.dispatch('carrierImportFetchSnapshot', {
          scrapeJobId: this.policies.scrapeJobId, ...current.filter, limit: PAGE, offset: this.offset,
        }).then((data) => {
          if (seq !== this.loadSeq) return;
          const rows = (data && data.items) || [];
          this.addSnapshotRows(rows);
          this.offset += rows.length;
          const total = Number(data && data.total) || 0;
          if (rows.length < PAGE || this.offset >= total) next();
        });
      } else {
        request = this.loadStopped().then((list) => {
          if (seq !== this.loadSeq) return;
          const q = this.search.trim().toLowerCase();
          this.items = list
            .filter((p) => !q || `${p.policyNumber} ${p.clientName || ''}`.toLowerCase().includes(q))
            .map(this.fromStopped);
          next();
        });
      }
      return request
        .catch((err) => {
          if (seq !== this.loadSeq) return;
          this.loadFailed = true;
          this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) });
        })
        .finally(() => {
          if (seq !== this.loadSeq) return;
          this.loading = false;
          this.$nextTick(this.watchBottom);
        });
    },
    watchBottom() {
      if (!this.observer) return;
      this.observer.disconnect();
      if (this.$refs.more) this.observer.observe(this.$refs.more);
    },
    download() {
      this.downloading = true;
      this.$store.dispatch('carrierImportExportRefundReceipts', {
        scrapeJobId: this.payments.scrapeJobId, jobType: this.payments.jobType,
      })
        .then(({ blob, fileName }) => saveFile(blob, fileName))
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.downloading = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
// Success text has no token in the app's palette -- the same literal ImportRunList uses.
$ink-success: #2e7d32;

.run-rows {
  &__subtitle {
    font-size: 13px;
    font-weight: 400;
    color: var(--solid-dark-purple);
    margin-bottom: 12px;
  }

  // BaseTabs floats; flow-root keeps it inside the drawer's flow.
  &__tabs {
    display: flow-root;
    margin-bottom: 12px;
  }

  &__search {
    margin-bottom: 8px;
  }

  &__empty {
    padding: 24px 0;
    font-size: 14px;
    color: var(--solid-dark-purple);
  }

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 0;
    border-bottom: 1px solid var(--solid-lavender-grey);

    &:last-of-type { border-bottom: 0; }
  }

  &__main { flex: 1; min-width: 0; }

  &__policy {
    font-size: 14px;
    font-weight: 600;
    color: var(--solid-black-purple);
  }

  &__policy-link {
    color: inherit;
    text-decoration: underline;
    text-decoration-color: var(--solid-lavender-grey);
    text-underline-offset: 2px;

    &:hover { color: var(--solid-purple); text-decoration-color: currentColor; }
  }

  &__client {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 2px;
  }

  &__why {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 6px;
  }

  &__changes {
    font-size: 12.5px;
    color: var(--solid-black-purple);
    margin-top: 6px;
    font-variant-numeric: tabular-nums;
  }

  &__failure {
    font-size: 12.5px;
    color: var(--solid-red);
    margin-top: 6px;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }

  &__tag {
    padding: 1px 8px;
    border-radius: 100px;
    font-size: 11.5px;
    background: var(--solid-lavender-ligth);
    color: var(--solid-dark-purple);
  }

  &__side {
    flex: none;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }

  &__result {
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;

    &--done { color: $ink-success; }
    &--ok { color: var(--solid-green); }
    &--warn { color: var(--solid-orange); }
    &--error { color: var(--solid-red); }
    &--muted { color: var(--solid-dark-purple); }
  }

  &__status {
    font-size: 12px;
    color: var(--solid-dark-purple);
    white-space: nowrap;
  }

  // Tall enough to be seen by the observer before the spinner is in it.
  &__more {
    display: flex;
    justify-content: center;
    min-height: 32px;
    margin-top: 12px;
  }
}
</style>
